import React, { useState } from "react";
import { FormControl } from "@/components/ui/FormControl";
import { Input } from "@/components/ui/Input";
import { CardSelector, useDonationFormStore } from "@/stores";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Stack } from "@/components/ui/Stack";
import { useTranslation } from "@/hooks/useTranslation";
import type { CardSelectorAddressFormProps } from "./CardSelectorProps";
import { Select } from "@/components/ui/Select";
import { countryNames } from "@/utils/countries";
import { Field } from "../Field";
import type { AddressFields } from "@/types/Address";
import * as z from "zod";

type AddressFieldConfig = {
  name: keyof AddressFields;
  serverErrorPath: string;
  errorKey: string;
  type?: "input" | "select";
};

const ADDRESS_FIELDS: AddressFieldConfig[] = [
  {
    name: "address-line-1",
    serverErrorPath: "card_selector.recipient.address_line_1",
    errorKey: "address-line-1-required",
  },
  {
    name: "address-line-2",
    serverErrorPath: "card_selector.recipient.address_line_2",
    errorKey: "address-line-2-required",
  },
  {
    name: "city",
    serverErrorPath: "card_selector.recipient.city",
    errorKey: "city-required",
  },
  {
    name: "region",
    serverErrorPath: "card_selector.recipient.region",
    errorKey: "region-required",
  },
  {
    name: "postal-code",
    serverErrorPath: "card_selector.recipient.postal_code",
    errorKey: "postal-code-required",
  },
  {
    name: "country",
    serverErrorPath: "card_selector.recipient.country",
    errorKey: "country-required",
    type: "select",
  },
];

export function CardSelectorAddressForm({
  config,
}: CardSelectorAddressFormProps) {
  const [selected, setSelected] = useState<boolean>(false);

  const t = useTranslation("card_selector");

  const setFieldValue = useDonationFormStore((state) => state.setFieldValue);

  const someAddressFieldIsRequired = Object.values(
    config["card-selector-address-fields"],
  ).some((field) => field["is-required"]);

  const createValidationSchema =
    (fieldName: keyof AddressFields, errorKey: string) => () => {
      if (config["card-selector-address-fields"][fieldName]["is-required"]) {
        return z.string().min(1, {
          message: t(`errors.${errorKey}`) as string,
        });
      }
      return null;
    };

  const renderInputField = (
    value: string,
    handleChange: (
      value: string,
      ev: React.ChangeEvent<HTMLInputElement>,
    ) => void,
    isValid: boolean | null,
    errorMessage: string | null,
    handleBlur: () => void,
    ref: React.Ref<HTMLInputElement>,
    fieldName: keyof AddressFields,
  ) => (
    <FormControl
      label={t(config["card-selector-address-fields"][fieldName].label.i18n)}
      error={errorMessage}
      isValid={isValid}
    >
      <Input
        name={fieldName}
        value={value}
        onChange={(ev) => handleChange(ev.target.value, ev)}
        onBlur={() => handleBlur()}
        ref={ref}
      />
    </FormControl>
  );

  const renderSelectField = (
    value: string,
    handleChange: (
      value: string,
      ev: React.ChangeEvent<HTMLSelectElement>,
    ) => void,
    isValid: boolean | null,
    errorMessage: string | null,
    handleBlur: () => void,
    ref: React.Ref<HTMLSelectElement>,
    fieldName: keyof AddressFields,
  ) => (
    <FormControl
      label={t(config["card-selector-address-fields"][fieldName].label.i18n)}
      error={errorMessage}
      isValid={isValid}
    >
      <Select
        name={fieldName}
        value={value}
        onChange={(ev) => handleChange(ev.target.value, ev)}
        onBlur={() => handleBlur()}
        ref={ref}
      >
        <option value=""></option>
        {countryNames.map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  const renderAddressField = (fieldConfig: AddressFieldConfig) => (
    <Field<CardSelector>
      key={fieldConfig.name}
      serverErrorPaths={[fieldConfig.serverErrorPath]}
      name={`postCardAddress.${fieldConfig.name}` as keyof CardSelector}
      validate={createValidationSchema(fieldConfig.name, fieldConfig.errorKey)}
      render={({
        value,
        handleChange,
        isValid,
        errorMessage,
        handleBlur,
        ref,
      }) => {
        if (fieldConfig.type === "select") {
          return renderSelectField(
            value,
            handleChange,
            isValid,
            errorMessage,
            handleBlur,
            ref as React.RefObject<HTMLSelectElement>,
            fieldConfig.name,
          );
        }

        return renderInputField(
          value,
          handleChange,
          isValid,
          errorMessage,
          handleBlur,
          ref as React.RefObject<HTMLInputElement>,
          fieldConfig.name,
        );
      }}
    />
  );

  if (selected) {
    return <Stack gap="md">{ADDRESS_FIELDS.map(renderAddressField)}</Stack>;
  }

  return (
    <Field<CardSelector>
      serverErrorPaths={ADDRESS_FIELDS.map((field) => field.serverErrorPath)}
      name="cardSelector.postalCode"
      validate={() => {
        if (someAddressFieldIsRequired) {
          return z.any().refine(() => false, {
            message: t("errors.should-select-address") as string,
          });
        }
        return null;
      }}
      render={({ errorMessage }) => (
        <FormControl
          label={t(`${config["card-selector-label-postal-code"].i18n}`)}
          error={errorMessage}
        >
          <AddressAutocomplete
            onSelect={(address) => {
              setSelected(true);
              setFieldValue("cardSelector.postCardAddress", address);
            }}
          />
        </FormControl>
      )}
    />
  );
}
